import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../../contexts/settings-context";
import { CalculationInfoIcon } from "../../accounting/CalculationInfoIcon";
import { CalculationGuideDialog } from "../../accounting/CalculationGuideDialog";

const ROLE_ORDER = [
  "montor",
  "montasjeleder",
  "prosjektleder",
  "prosjektering",
  "innleid",
];

const num = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Enterprise calc summary from API `summary` (authoritative BigDecimal strings).
 * Shown in workspace context when no line/chapter is selected.
 */
export default function OfferCalculationSummaryPanel({ summary = null }) {
  const { t } = useTranslation();
  const { priceFormatter } = useSettings();
  const [guideKey, setGuideKey] = useState(null);

  const parsed = useMemo(() => {
    if (!summary || typeof summary !== "object") return null;
    return {
      materialCost: num(summary.material_cost),
      materialSales: num(summary.material_sales),
      laborCost: num(summary.labor_cost),
      laborSales: num(summary.labor_sales),
      hoursTotal: num(summary.hours_total),
      hoursByRole: summary.hours_by_role || {},
      costTotal: num(summary.cost_total),
      salesTotal: num(summary.sales_total),
      db: num(summary.db ?? summary.contribution_margin),
      dg: num(summary.dg_percent ?? summary.contribution_margin_percent),
    };
  }, [summary]);

  if (!parsed) {
    return (
      <div className="offer-calc-workspace__pane-empty offer-calc-workspace__pane-empty--compact">
        <p className="offer-calc-workspace__pane-empty-title">
          {t("offer.calculation.workspace.context_empty_title")}
        </p>
        <p className="offer-calc-workspace__pane-empty-hint">
          {t("offer.calculation.workspace.context_empty_hint")}
        </p>
      </div>
    );
  }

  const fmtMoney = (value) =>
    value == null ? "—" : priceFormatter.format(value);
  const fmtHours = (value) =>
    value == null ? "—" : Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });

  const rows = [
    {
      key: "hours",
      label: t("offer.calculation.summary.hours_total"),
      value: fmtHours(parsed.hoursTotal),
      guide: "hours",
    },
    {
      key: "material",
      label: t("offer.calculation.summary.material_cost"),
      value: fmtMoney(parsed.materialCost),
      guide: "material",
    },
    {
      key: "labor",
      label: t("offer.calculation.summary.labor_cost"),
      value: fmtMoney(parsed.laborCost),
      guide: "labor",
    },
    {
      key: "cost",
      label: t("offer.calculation.summary.cost_total"),
      value: fmtMoney(parsed.costTotal),
      guide: "cost",
    },
    {
      key: "db",
      label: t("offer.calculation.summary.db"),
      value: fmtMoney(parsed.db),
      guide: "db",
      className:
        parsed.db != null && parsed.db >= 0 ? "is-positive" : "is-negative",
    },
    {
      key: "dg",
      label: t("offer.calculation.summary.dg"),
      value: parsed.dg == null ? "—" : `${parsed.dg.toFixed(1)} %`,
      guide: "dg",
      className:
        parsed.dg != null && parsed.dg >= 0 ? "is-positive" : "is-negative",
      strong: true,
    },
    {
      key: "sales",
      label: t("offer.calculation.summary.sales_total"),
      value: fmtMoney(parsed.salesTotal),
      guide: "sales",
      strong: true,
    },
  ];

  const roleRows = ROLE_ORDER.map((role) => {
    const hours = num(parsed.hoursByRole[role]);
    if (hours == null || !(hours > 0)) return null;
    return (
      <div key={role}>
        <dt>{t(`offer.calculation.summary.role.${role}`)}</dt>
        <dd className="tabular-nums">{fmtHours(hours)}</dd>
      </div>
    );
  }).filter(Boolean);

  return (
    <>
      <div className="offer-calc-context__headline">
        <div className="offer-calc-context__line-title">
          {t("offer.calculation.summary.title")}
        </div>
        <div className="offer-calc-context__line-meta">
          {t("offer.calculation.summary.subtitle")}
        </div>
      </div>

      <dl className="offer-calc-context__props">
        {rows.map((row) => (
          <div key={row.key}>
            <dt>
              <span className="inline-flex items-center gap-1">
                {row.label}
                <CalculationInfoIcon
                  ariaLabel={t("offer.calculation.summary.info_aria", {
                    label: row.label,
                  })}
                  onClick={() => setGuideKey(row.guide)}
                />
              </span>
            </dt>
            <dd
              className={[
                "tabular-nums",
                row.strong ? "offer-calc-context__props-strong" : "",
                row.className || "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {row.value}
            </dd>
          </div>
        ))}
      </dl>

      {roleRows.length > 0 ? (
        <div className="offer-calc-context__card">
          <div className="offer-calc-context__card-title">
            {t("offer.calculation.summary.hours_by_role")}
          </div>
          <dl className="offer-calc-context__props">{roleRows}</dl>
        </div>
      ) : null}

      <CalculationGuideDialog
        open={Boolean(guideKey)}
        onClose={() => setGuideKey(null)}
        titleKey={
          guideKey
            ? `offer.calculation.summary.guide.${guideKey}.title`
            : null
        }
        bodyKey={
          guideKey
            ? `offer.calculation.summary.guide.${guideKey}.body`
            : null
        }
      />
    </>
  );
}
